// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Zone from "@insite/client-framework/Components/Zone";
import {
    DoNotFollowLinks,
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Modals from "@insite/content-library/Components/Modals";
import MobiusPage from "@insite/mobius/Page";
import * as React from "react";

// XNG-Changes: custom fields for Article pages
const enum fields {
    isArticlePage = "isArticlePage",
    articleTags = "articleTags",
    difficulty = "difficulty",
    author = "author",
    articleImage = "articleImage",
    articleContents = "articleContents",
    summary = "summary",
    isFeatured = "isFeatured",
    publishDate = "publishDate",
    overrideTitle = "overrideTitle",
}
export interface Tag {
    fields: {
        tag: string;
    };
}
interface OwnProps extends PageProps {
    fields: {
        [fields.isArticlePage]: boolean;
        [fields.articleTags]: Tag[];
        [fields.difficulty]: string;
        [fields.author]: string;
        [fields.articleImage]: string;
        [fields.articleContents]: string;
        [fields.summary]: string;
        [fields.publishDate]: string;
        [fields.isFeatured]: boolean;
        [fields.overrideTitle]: string;
    };
}

const Page = ({ id }: OwnProps) => (
    <MobiusPage>
        <Zone contentId={id} zoneName="Content" requireRows />
        <Modals />
    </MobiusPage>
);

const pageModule: PageModule = {
    component: Page,
    definition: {
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            // XNG-Changes : AWCS-263 : Remove OOTB Structured Data
            // StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            DoNotFollowLinks,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,
            // XNG-Change: Add Custom Fields
            {
                name: fields.overrideTitle,
                displayName: "Page Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 148,
                isRequired: true,
            },
            {
                name: fields.isArticlePage,
                displayName: "Is Article Page",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 149,
            },
            {
                name: fields.author,
                displayName: "Author",
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                sortOrder: 150,
                isVisible: page => page.fields.isArticlePage,
            },
            {
                name: fields.publishDate,
                displayName: "Publish Date",
                defaultValue: null,
                editorTemplate: "DateTimeField",
                fieldType: "General",
                sortOrder: 151,
                isVisible: page => page.fields.isArticlePage,
            },
            {
                name: fields.summary,
                displayName: "Summary",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "General",
                sortOrder: 152,
                isVisible: page => page.fields.isArticlePage,
            },
            {
                name: fields.articleContents,
                displayName: "Article Contents",
                defaultValue: "",
                editorTemplate: "RichTextField",
                fieldType: "General",
                isRequired: true,
                sortOrder: 153,
                isVisible: page => page.fields.isArticlePage,
            },

            {
                name: fields.isFeatured,
                editorTemplate: "CheckboxField",
                isVisible: page => page.fields.isArticlePage,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 154,
            },
            {
                name: fields.articleTags,
                editorTemplate: "ListField",
                isVisible: page => page.fields.isArticlePage,
                getDisplay: ({ fields: { tag } }) => tag || "Tag",
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "tag",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                sortOrder: 155,
            },
            {
                name: fields.difficulty,
                defaultValue: "",
                editorTemplate: "TextField",
                fieldType: "General",
                sortOrder: 156,
                isVisible: page => page.fields.isArticlePage,
            },
            {
                name: fields.articleImage,
                displayName: "Article Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 157,
                isVisible: page => page.fields.isArticlePage,
            },
        ],
    },
};

export default pageModule;
